import React, { useEffect, useRef, useState } from "react";

import {
  FOCUS_RING,
  TRANSITION_COLORS,
} from "../../SideMenu/sideMenuDensity";

export type HeaderMenuProps = {
  /** Accessible name for the trigger — "Account menu", "Settings menu". */
  label: string;
  /** Trigger contents: an icon, or the identity avatar. */
  trigger: React.ReactNode;
  /**
   * Panel width. The two header menus size differently — the account panel is
   * bound to the rail's width so it reads as the same object that used to live
   * there, the settings list only needs its longest label.
   */
  panelWidthClass: string;
  /**
   * Trigger shape. Defaults to the 28px circle the identity avatar wears; a
   * control whose trigger carries a word rather than a glyph passes its own,
   * since a label does not fit a circle. Only the shape is the caller's — the
   * dismissal contract below stays here, which is the reason this component
   * exists.
   */
  triggerClassName?: string;
  /** Receives a dismiss callback so a row can close the menu after acting. */
  children: (dismiss: () => void) => React.ReactNode;
};

/* 28px to match the title's own line box. At 32 the button was the tallest
   thing in the row and the header grew 4px on every route. */
const TRIGGER_CIRCLE = "h-7 w-7 justify-center rounded-full";

/**
 * Icon trigger plus the panel it opens, anchored to the trigger's right edge.
 *
 * Exists so the header's two controls (CTX-636, CTX-637) share one dismissal
 * contract instead of two copies of the same listeners. The panel closes on an
 * outside pointer-down and on Escape; both are registered in the capture phase
 * so a row inside an overlay cannot swallow them.
 *
 * Anchored right, unlike the rail's card, because these sit at the end of the
 * header: left-anchored panels would hang off the viewport.
 */
export default function HeaderMenu({
  label,
  trigger,
  panelWidthClass,
  triggerClassName = TRIGGER_CIRCLE,
  children,
}: HeaderMenuProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const dismiss = () => setOpen(false);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (e: PointerEvent) => {
      if (rootRef.current?.contains(e.target as Node)) return;
      dismiss();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") dismiss();
    };

    window.addEventListener("pointerdown", onPointerDown, { capture: true });
    window.addEventListener("keydown", onKey, { capture: true });
    return () => {
      window.removeEventListener(
        "pointerdown",
        onPointerDown as EventListener,
        { capture: true } as EventListenerOptions
      );
      window.removeEventListener(
        "keydown",
        onKey as EventListener,
        { capture: true } as EventListenerOptions
      );
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        className={`flex items-center ${triggerClassName} ${TRANSITION_COLORS} ${FOCUS_RING} hover:bg-semantic-state-active`}
      >
        {trigger}
      </button>

      {open && (
        <div
          className={`absolute right-0 top-full z-[60] mt-2 ${panelWidthClass} overflow-hidden rounded-lg border border-semantic-border-secondary bg-semantic-bg-primary shadow-xl`}
        >
          {children(dismiss)}
        </div>
      )}
    </div>
  );
}
